// c/Data/Fullstack/MyProjects/invest-ai/invest_FE/src/utils/inquiryMatcher.js

/**
 * Converts experience level strings to a numerical rank for comparison.
 * Higher rank is more experienced.
 * @param {string} level - The experience level string.
 * @returns {number} The numerical rank.
 */
const getExperienceRank = (level) => {
  const ranks = {
    All: 0,
    'This is my first one': 1,
    '1+ properties': 2,
    '1-2 properties': 2,
    '3+ properties': 3,
    '3-5 properties': 3,
    '6+ properties': 4,
    '6-10 properties': 4,
    '11+ properties': 5,
  };
  return ranks[level] || 0;
};

/**
 * Converts purchase timeline strings to a numerical rank for comparison.
 * Lower rank is a more immediate timeline.
 * @param {string} timeline - The purchase timeline string.
 * @returns {number} The numerical rank.
 */
const getTimelineRank = (timeline) => {
  const ranks = {
    Immediately: 1,
    'Within 3 months': 2,
    'Within 6 months': 3,
    'Just Browsing': 4,
    All: 5,
  };
  return ranks[timeline] || 5;
};

/**
 * Parses the maximum value from a budget string.
 * @param {string} budgetString - The budget string (e.g., "$100,000 - $250,000").
 * @returns {number} The maximum budget value, or Infinity.
 */
const getMaxBudget = (budgetString) => {
  if (!budgetString) return 0;
  if (budgetString.includes('+')) return Infinity;

  const numbers = budgetString.match(/\d+/g)?.map(Number);
  if (!numbers) return 0;

  return Math.max(...numbers) * 1000; // Assuming budgets are in thousands
};

/**
 * Compares a buyer's profile against a property's inquiry requirements.
 * @param {object} user - The buyer's user object, including `investmentProfile`.
 * @param {object} property - The property object, including `inquiryRequirements` and `price`.
 * @returns {object} An object containing the analysis results.
 */
export const analyzeInquiryMatch = (user, property) => {
  const profile = user?.investmentProfile;
  const requirements = property?.inquiryRequirements;

  if (!profile || !requirements) {
    // If data is missing, default to a "match" to not block inquiries unnecessarily.
    // The seller can still see the buyer's profile.
    return {
      experience: true,
      timeline: true,
      involvement: true,
      allCash: true,
      budget: true,
      mismatchCount: 0,
    };
  }

  // --- Perform Comparisons ---

  // Experience: Buyer's rank must be >= required rank.
  const experienceMatch = requirements.minExperienceLevel === 'All' || getExperienceRank(profile.experienceLevel) >= getExperienceRank(requirements.minExperienceLevel);

  // Timeline: Buyer's rank must be <= required rank (lower is better).
  const timelineMatch = requirements.minPurchaseTimeline === 'All' || getTimelineRank(profile.purchaseTimeline) <= getTimelineRank(requirements.minPurchaseTimeline);

  // Involvement: Buyer's level must be >= required level.
  const involvementMatch = (profile.desiredInvolvement || 1) >= (requirements.minInvolvementLevel || 1);

  // All Cash: If required, buyer must have 'All Cash' financing.
  const allCashMatch = !requirements.requiresAllCash || profile.financingMethod === 'All Cash';

  // Budget: Property price must be <= buyer's max budget.
  const budgetMatch = property.price <= getMaxBudget(profile.investmentBudget);

  // --- Compile Results ---

  const analysis = {
    experience: experienceMatch,
    timeline: timelineMatch,
    involvement: involvementMatch,
    allCash: allCashMatch,
    budget: budgetMatch,
  };

  // Calculate the mismatch count for the three "hard filter" criteria.
  const mismatchCount = [experienceMatch, timelineMatch, involvementMatch].filter((match) => !match).length;

  return {
    ...analysis,
    mismatchCount,
  };
};
